import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  TablePagination,
  Avatar,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  MoreVert,
  WhatsApp,
  Telegram,
  Instagram,
  SmartToy,
  Person,
  Download,
  FileDownload,
  Summarize,
  Article
} from '@mui/icons-material';
import { analyticsApi } from '../../services/api';

const ChatTable = ({ logs = [], total = 0, loading = false, filters = {}, onFilterChange, onViewMessages, onSummaryUpdate }) => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [summaryDialog, setSummaryDialog] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [summaryError, setSummaryError] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleViewMessages = () => {
    if (selectedRow && onViewMessages) {
      onViewMessages(selectedRow);
    }
    handleMenuClose();
  };

  const handleExportAllConversations = async (format = 'csv') => {
    try {
      setExporting(true);
      setExportError(null);
      
      // Export all conversations with current filters
      // Only include parameters that have actual values (not undefined/null/empty)
      const exportParams = {};
      
      if (filters.platform && filters.platform !== '') {
        exportParams.platform = filters.platform;
      }
      if (filters.sentiment && filters.sentiment !== '') {
        exportParams.sentiment = filters.sentiment;
      }
      if (filters.query && filters.query !== '') {
        exportParams.query = filters.query;
      }
      if (filters.startDate && filters.startDate !== '') {
        exportParams.start_date = filters.startDate;
      }
      if (filters.endDate && filters.endDate !== '') {
        exportParams.end_date = filters.endDate;
      }
      
      console.log('Export all conversations params:', exportParams);
      
      await analyticsApi.exportConversations(format, exportParams);
      
      setExportSuccess(true);
      handleMenuClose();
    } catch (error) {
      console.error('Export all conversations error:', error);
      let errorMessage = 'Errore durante l\'esportazione delle conversazioni';
      
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Accesso negato. Effettua il login per continuare.';
        } else if (error.response.status === 403) {
          errorMessage = 'Non hai i permessi necessari per esportare i dati.';
        } else if (error.response.status === 422) {
          errorMessage = 'Parametri non validi per l\'esportazione.';
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setExportError(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const handleExportConversationMessages = async (conversationId, format = 'csv') => {
    try {
      setExporting(true);
      setExportError(null);
      
      console.log('Export conversation messages for ID:', conversationId);
      
      await analyticsApi.exportConversationMessages(conversationId, format);
      
      setExportSuccess(true);
      handleMenuClose();
    } catch (error) {
      console.error('Export conversation messages error:', error);
      let errorMessage = 'Errore durante l\'esportazione dei messaggi';
      
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Accesso negato. Effettua il login per continuare.';
        } else if (error.response.status === 403) {
          errorMessage = 'Non hai i permessi necessari per esportare i dati.';
        } else if (error.response.status === 404) {
          errorMessage = 'Conversazione non trovata o senza messaggi.';
        } else if (error.response.status === 422) {
          errorMessage = 'Parametri non validi per l\'esportazione.';
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setExportError(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const handleShowSummary = (conversation) => {
    setSelectedConversation(conversation);
    setSummaryText(conversation.summary || '');
    setSummaryDialog(true);
    handleMenuClose();
  };

  const handleGenerateSummary = async () => {
    if (!selectedConversation) return;
    
    try {
      setGeneratingSummary(true);
      setSummaryError(null);
      
      const response = await analyticsApi.generateConversationSummary(selectedConversation.id, i18n.language);
      
      // Update the summary text
      setSummaryText(response.summary);
      
      // Update the selected conversation
      setSelectedConversation(prev => ({
        ...prev,
        summary: response.summary
      }));
      
      // Update the parent component's state if callback is provided
      if (onSummaryUpdate) {
        onSummaryUpdate(selectedConversation.id, response.summary);
      }
      
    } catch (error) {
      console.error('Generate summary error:', error);
      let errorMessage = 'Errore durante la generazione del riassunto';
      
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Accesso negato. Effettua il login per continuare.';
        } else if (error.response.status === 403) {
          errorMessage = 'Non hai i permessi necessari per generare riassunti.';
        } else if (error.response.status === 404) {
          errorMessage = 'Conversazione non trovata.';
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSummaryError(errorMessage);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleCloseSummaryDialog = () => {
    setSummaryDialog(false);
    setSelectedConversation(null);
    setSummaryText('');
    setSummaryError(null);
  };

  const handleChangePage = (event, newPage) => {
    onFilterChange('page', newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onFilterChange('pageSize', parseInt(event.target.value, 10));
    onFilterChange('page', 0);
  };

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'whatsapp':
        return <WhatsApp sx={{ color: '#25D366', fontSize: 20 }} />;
      case 'telegram':
        return <Telegram sx={{ color: '#0088cc', fontSize: 20 }} />;
      case 'instagram':
        return <Instagram sx={{ color: '#E4405F', fontSize: 20 }} />;
      default:
        return <SmartToy sx={{ color: '#666', fontSize: 20 }} />;
    }
  };

  const formatUsername = (log) => {
    // Priority: username > first_name + last_name > user_id
    if (log.username) {
      return log.username;
    }
    
    const firstName = log.first_name || '';
    const lastName = log.last_name || '';
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    
    return `User ${log.user_id || 'Anonymous'}`;
  };

  const getUserAvatar = (log) => {
    if (log.username) {
      return log.username.substring(0, 2).toUpperCase();
    }
    
    const firstName = log.first_name || '';
    const lastName = log.last_name || '';
    
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    } else if (lastName) {
      return lastName.substring(0, 2).toUpperCase();
    }
    
    return log.user_id ? String(log.user_id).substring(0, 2).toUpperCase() : 'U';
  };

  const getActivityStatusChip = (isActive) => {
    return (
      <Chip
        label={isActive ? "Active" : "Inactive"}
        size="small"
        sx={{
          bgcolor: isActive ? '#e8f5e8' : '#ffebee',
          color: isActive ? '#4caf50' : '#f44336',
          fontWeight: 500
        }}
      />
    );
  };

  const getMessageTypeChip = (messageCount, aiResponseCount) => {
    const userCount = messageCount - aiResponseCount;
    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Chip
          icon={<Person />}
          label={`${userCount}`}
          size="small"
          sx={{
            bgcolor: '#f3e5f5',
            color: '#7b1fa2',
            '& .MuiChip-icon': { color: '#7b1fa2' }
          }}
        />
        <Chip
          icon={<SmartToy />}
          label={`${aiResponseCount}`}
          size="small"
          sx={{
            bgcolor: '#e3f2fd',
            color: '#1976d2',
            '& .MuiChip-icon': { color: '#1976d2' }
          }}
        />
      </Box>
    );
  };

  const getSentimentChip = (sentiment) => {
    const sentimentConfig = {
      positive: { color: '#4caf50', bg: '#e8f5e8', label: 'Positive' },
      negative: { color: '#f44336', bg: '#ffebee', label: 'Negative' },
      neutral: { color: '#9e9e9e', bg: '#f5f5f5', label: 'Neutral' }
    };

    const config = sentimentConfig[sentiment?.toLowerCase()] || sentimentConfig.neutral;
    
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bg,
          color: config.color,
          fontWeight: 500
        }}
      />
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  const paginatedLogs = logs;

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Export All Conversations Button */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="h2">
          Conversations
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownload />}
            onClick={() => handleExportAllConversations('csv')}
            disabled={exporting}
            size="small"
          >
            {exporting ? 'Exporting...' : 'Export Records'}
          </Button>
        </Box>
      </Box>
      
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('conversations.user')}</TableCell>
              <TableCell>{t('conversations.platform')}</TableCell>
              <TableCell>{t('conversations.titleTopic')}</TableCell>
              <TableCell>{t('conversations.summary')}</TableCell>
              <TableCell>{t('conversations.messages')}</TableCell>
              <TableCell>{t('conversations.status')}</TableCell>
              <TableCell>{t('conversations.sentiment')}</TableCell>
              <TableCell>{t('conversations.started')}</TableCell>
              <TableCell>{t('conversations.lastActivity')}</TableCell>
              <TableCell align="center">{t('conversations.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress />
                  <Typography>{t('conversations.loadingConversations')}</Typography>
                </TableCell>
              </TableRow>
            ) : paginatedLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2">{t('conversations.noConversations')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                        {getUserAvatar(log)}
                      </Avatar>
                      <Typography variant="body2">
                        {formatUsername(log)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getPlatformIcon(log.platform)}
                      <Chip 
                        label={log.platform ? log.platform.charAt(0).toUpperCase() + log.platform.slice(1) : 'N/A'} 
                        size="small"
                        variant="outlined"
                        sx={{
                          textTransform: 'capitalize',
                          fontWeight: 500
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {truncateText(log.title || 'Untitled Conversation', 50)}
                      </Typography>
                      {log.topic && (
                        <Typography variant="caption" color="text.secondary">
                          {truncateText(log.topic, 40)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      {log.summary ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                            {truncateText(log.summary, 100)}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<Article />}
                            onClick={() => handleShowSummary(log)}
                            sx={{ textTransform: 'none' }}
                          >
                            {t('conversations.viewFullSummary')}
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          startIcon={<Summarize />}
                          onClick={() => handleShowSummary(log)}
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                        >
                          {t('conversations.generateSummary')}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {log.message_count || 0} total
                      </Typography>
                      {getMessageTypeChip(log.message_count || 0, log.ai_response_count || 0)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getActivityStatusChip(log.is_active)}
                  </TableCell>
                  <TableCell>
                    {getSentimentChip(log.sentiment)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatTimestamp(log.started_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatTimestamp(log.last_message_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, log)}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={total}
        rowsPerPage={filters.pageSize || 10}
        page={filters.page || 0}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows per page:"
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewMessages}>View Messages</MenuItem>
        <MenuItem onClick={() => handleShowSummary(selectedRow)}>
          <Summarize sx={{ mr: 1 }} />
          {selectedRow?.summary ? 'View Summary' : 'Generate Summary'}
        </MenuItem>
        <MenuItem 
          onClick={() => handleExportConversationMessages(selectedRow?.id, 'csv')}
          disabled={exporting || !selectedRow?.id}
        >
          <Download sx={{ mr: 1 }} />
          {exporting ? 'Exporting...' : 'Export Messages'}
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>View Analytics</MenuItem>
      </Menu>
      
      {/* Success Snackbar */}
      <Snackbar
        open={exportSuccess}
        autoHideDuration={6000}
        onClose={() => setExportSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setExportSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          ✅ Export completed successfully! File has been downloaded.
        </Alert>
      </Snackbar>
      
      {/* Error Snackbar */}
      <Snackbar
        open={!!exportError}
        autoHideDuration={8000}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setExportError(null)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {exportError}
        </Alert>
      </Snackbar>

      {/* Summary Dialog */}
      <Dialog
        open={summaryDialog}
        onClose={handleCloseSummaryDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Summarize />
            {t('conversations.summaryDialog', 'Conversation Summary')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedConversation && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedConversation.title || t('conversations.untitled', 'Nuova conversazione')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {formatUsername(selectedConversation)} • {selectedConversation.platform} • {formatTimestamp(selectedConversation.started_at)}
              </Typography>
              
              {/* Message count information */}
              {selectedConversation.message_count && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('conversations.totalMessages', 'Total messages')}: {selectedConversation.message_count}
                  {selectedConversation.summary && (
                    <span>
                      {' • '}
                      {t('conversations.summarizedMessages', 'Summarized')}: {Math.min(selectedConversation.message_count, 100)}
                      {selectedConversation.message_count > 100 && (
                        <span style={{ color: '#f57c00' }}>
                          {' '}({selectedConversation.message_count - 100} {t('conversations.moreMessages', 'more messages available')})
                        </span>
                      )}
                    </span>
                  )}
                </Typography>
              )}
              
              {summaryError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {summaryError}
                </Alert>
              )}
              
              {selectedConversation.summary ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1">
                    {selectedConversation.summary}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('conversations.noSummary', 'No summary available for this conversation.')}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={generatingSummary ? <CircularProgress size={20} /> : <Summarize />}
                    onClick={handleGenerateSummary}
                    disabled={generatingSummary}
                    sx={{ mt: 1 }}
                  >
                    {generatingSummary ? t('conversations.generating', 'Generating...') : t('conversations.generateSummary')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSummaryDialog}>
            {t('common.close')}
          </Button>
          {selectedConversation?.summary && selectedConversation.message_count > 100 && (
            <Button
              startIcon={generatingSummary ? <CircularProgress size={20} /> : <Summarize />}
              onClick={handleGenerateSummary}
              disabled={generatingSummary}
            >
              {generatingSummary ? t('conversations.regenerating', 'Regenerating...') : t('conversations.regenerateSummary', 'Regenerate Summary')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ChatTable;
